import { useEffect } from "react";
import { useLocation } from "react-router";
import { useArgusConnectionContext } from "../../providers/argus-connection-provider";

/**
 * Global keyboard shortcuts for the Argus sidebar.
 *
 * - `Cmd/Ctrl + /` toggles the sidebar (no-op on `/argus`)
 * - `Escape` closes the sidebar when it's open
 *
 * Mounted alongside `<ArgusSidebar />` in App.tsx. Renders nothing.
 * Handlers are attached to `window` so they work regardless of which
 * element currently has focus.
 */
export const ArgusKeyboardShortcuts = () => {
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useArgusConnectionContext();
  const location = useLocation();

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      // Don't steal keystrokes from contenteditable / inputs on single keys.
      const target = event.target as HTMLElement | null;
      const isTyping =
        !!target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);

      // Cmd/Ctrl + / — toggle sidebar.
      if ((event.metaKey || event.ctrlKey) && event.key === "/") {
        if (location.pathname.startsWith("/argus")) return;
        event.preventDefault();
        toggleSidebar();
        return;
      }

      // Escape — close sidebar (only when not typing, to avoid hijacking
      // natural Escape-to-blur behaviour inside the sidebar's own input).
      if (event.key === "Escape" && sidebarOpen && !isTyping) {
        event.preventDefault();
        setSidebarOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [sidebarOpen, toggleSidebar, setSidebarOpen, location.pathname]);

  return null;
};
